import { useState, useCallback } from "react";
import {
  Box,
  Button,
  Popover,
  Stack,
  TextField,
  FormLabel,
} from "@mui/material";
import type { BoxProps } from "@mui/material/Box";
import LoadingButton from "@mui/lab/LoadingButton";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTrueBizApi } from "../../../api";
import { useIdentity } from "../../IdentityProvider";
import { useShowNetworkFailureToast } from "../../NetworkFailureToast";
import {
  APIMonitoringAnomaly,
  APIAnomalyActivityType,
} from "../../../types/APIAnomaly";
import { APIMonitoringEnrollment } from "../../../types/APIMonitoringEnrollment";

export interface Props extends BoxProps {
  issue: APIMonitoringAnomaly;
  monitorDomain?: string;
  allPeopleQuery: any;
}

export default function CommentButton({
  issue,
  monitorDomain,
  allPeopleQuery,
  ...etc
}: Props) {
  const disabled = allPeopleQuery.isLoading;

  const api = useTrueBizApi();
  const identity = useIdentity();
  const queryClient = useQueryClient();
  const showToast = useShowNetworkFailureToast();

  const [commentText, setCommentText] = useState("");

  const [el, setEl] = useState<HTMLElement | null>(null);

  const onCancel = useCallback(() => {
    setEl(null);
    setCommentText("");
  }, []);

  const commentMutation = useMutation({
    mutationKey: ["issue", "comment", issue.id, commentText],
    mutationFn: () => api.addAnomalyComment(issue.id, commentText),
    onSuccess: () => {
      setCommentText("");
      setEl(null);

      queryClient.invalidateQueries({
        queryKey: ["getNotification", issue.id],
      });

      queryClient.invalidateQueries({
        queryKey: ["getMonitor", issue.monitors[0]?.domain],
      });
    },
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: ["getNotification", issue.id],
      });

      await queryClient.cancelQueries({
        queryKey: ["getMonitor", issue.monitors[0]?.domain],
      });

      const previousNotification = queryClient.getQueryData([
        "getNotification",
        issue.id,
      ]);

      const previousMonitor = queryClient.getQueryData([
        "getMonitor",
        issue.monitors[0]?.domain,
      ]);

      const now = new Date().toISOString();
      const me = (allPeopleQuery.data || []).filter(
        (p: any) => p.email != null && p.email === identity.user?.email
      )[0];

      if (previousNotification) {
        queryClient.setQueryData(
          ["getNotification", issue.id],
          (old: APIMonitoringAnomaly) => {
            const updated = {
              ...old,
              comments: [
                ...old.comments,
                {
                  id: "PENDING",
                  created_at: now,
                  updated_at: now,
                  commenter: me || { id: "PENDING", email: null },
                  message: commentText,
                },
              ],
              activity: [
                ...old.activity,
                {
                  id: "PENDING",
                  type: APIAnomalyActivityType.COMMENTED,
                  created_at: now,
                  comment: {
                    id: "PENDING",
                    created_at: now,
                    updated_at: now,
                    commenter: me || { id: "PENDING", email: null },
                    message: commentText,
                  },
                },
              ],
            };

            return updated;
          }
        );
      }

      if (previousMonitor) {
        queryClient.setQueryData(
          ["getMonitor", issue.monitors[0]?.domain],
          (old: APIMonitoringEnrollment) => {
            const updated = {
              ...old,
              anomalies: old.anomalies.map((anomaly) => {
                if (anomaly.id !== issue.id) return anomaly;
                return {
                  ...anomaly,
                  comments: [
                    ...anomaly.comments,
                    {
                      id: "PENDING",
                      created_at: now,
                      updated_at: now,
                      commenter: me || { id: "PENDING", email: null },
                      message: commentText,
                    },
                  ],
                  activity: [
                    ...anomaly.activity,
                    {
                      id: "PENDING",
                      type: APIAnomalyActivityType.COMMENTED,
                      created_at: now,
                      comment: {
                        id: "PENDING",
                        created_at: now,
                        updated_at: now,
                        commenter: me || { id: "PENDING", email: null },
                        message: commentText,
                      },
                    },
                  ],
                };
              }),
            };

            return updated;
          }
        );
      }

      return { previousNotification, previousMonitor };
    },
    onError: (err, variables, context) => {
      if (context?.previousNotification) {
        queryClient.setQueryData(
          ["getNotification", issue.id],
          context.previousNotification
        );
      }

      if (context?.previousMonitor) {
        queryClient.setQueryData(
          ["getMonitor", issue.monitors[0]?.domain],
          context.previousMonitor
        );
      }

      showToast();
    },
  });

  return (
    <Box {...etc}>
      <Button
        disabled={disabled}
        onClick={(e: any) => {
          setEl(e.currentTarget);
        }}
        variant="outlined"
      >
        Comment
      </Button>
      <Popover
        open={!disabled && el != null}
        anchorEl={el}
        onClose={commentMutation.isPending ? undefined : onCancel}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Box sx={{ padding: 4, width: "400px" }}>
          <Stack>
            <FormLabel>Comment on this issue:</FormLabel>
            <TextField
              sx={{ width: "100%" }}
              multiline
              rows={3}
              value={commentText}
              onChange={(e) => {
                setCommentText(e.target.value);
              }}
              disabled={commentMutation.isPending || allPeopleQuery.isLoading}
            />
          </Stack>
          <Stack>
            <div
              style={{
                display: "flex",
                gap: "1em",
                justifyContent: "end",
                marginTop: "1em",
              }}
            >
              <Button
                variant="outlined"
                disabled={commentMutation.isPending}
                onClick={onCancel}
              >
                Cancel
              </Button>
              <LoadingButton
                loading={commentMutation.isPending}
                disabled={!commentText}
                variant="contained"
                onClick={async () => {
                  await commentMutation.mutateAsync();
                }}
              >
                Submit
              </LoadingButton>
            </div>
          </Stack>
        </Box>
      </Popover>
    </Box>
  );
}
